/**
 * <RoleTransitionAlertDialog> — Q65 confirmation before PATCH.
 *
 * Phase-2 Wave-4 W4.T6. Renders an AlertDialog asking the actor to
 * confirm a role / department transition before the parent modal
 * dispatches the PATCH. The four scenarios per research-A §2.2 + PLAN
 * T12.1/T12.2/T12.3 are handled inside via conditional copy:
 *
 *   - Promote to Admin (or Supervisor)
 *   - Promote to РО (MANAGER-tier)
 *   - Demote a РО to Сотрудник
 *   - Department transfer
 *
 * The component is purely presentational — opening / closing is owned by
 * the parent (employees-detail.modal). On confirm we call `onConfirm()`;
 * on cancel we call `onCancel()` and the parent reverts the role / dept
 * select back to its server value so the form is no longer dirty.
 *
 * ⚠️ Фикс-пак Р-114 (03.09.2026) — УБРАНА ВЕТКА «ДОПОЛНИТЕЛЬНЫЙ РО», И
 * УБРАНА ПОТОМУ, ЧТО ОПИСЫВАЛА МИР, ЗАПРЕЩЁННЫЙ БАЗОЙ. Прежняя редакция
 * (devil-advocate A9) при занятом отделе писала «получит права руководителя
 * В ДОПОЛНЕНИЕ к текущему» и предлагала «подтвердить перевод» — двух
 * ДЕЙСТВУЮЩИХ РО у отдела быть не может: триггер `user_one_active_ro_per_dept`
 * (миграция 002) отвергает вторую строку с `ERRCODE = 'unique_violation'`.
 * То есть диалог обещал исход, который база гарантированно отвергнет.
 *
 * Вместе с веткой убран и довод `existingManagerName`: родитель НИКОГДА его
 * не передавал (`setPendingTransition` в `employees-detail.modal.tsx` ключа
 * не заполняет), поэтому на занятом отделе показывалась ВТОРАЯ ветка — «Это
 * создаст нового РО», прямо перед отказом. Занятый отдел теперь отбивается
 * ДО открытия диалога — предпроверкой `hasActiveManager` в `onSubmit`
 * родителя, — так что до этого места случай не доходит вовсе.
 */
import * as React from 'react';

import { ROLE_ACCESS_LEVEL, type RoleSlug } from '@aris/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { rightsRu } from '@/i18n';

export interface RoleTransitionContext<TPending = unknown> {
  fromRoleSlug: RoleSlug;
  toRoleSlug: RoleSlug;
  fromRoleName: string;
  toRoleName: string;
  /** Display label for the user being edited. */
  targetFullName: string;
  /** Department names — null means "no department". */
  fromDeptName: string | null;
  toDeptName: string | null;
  /** Pending form values the parent will dispatch on confirm. */
  pending: TPending;
}

export interface RoleTransitionAlertDialogProps {
  open: boolean;
  context: RoleTransitionContext | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Pure helper exported for unit tests — derives a Russian copy block
 * from the transition context. Returns `{ title, description }`.
 */
export function describeTransition(ctx: RoleTransitionContext): {
  title: string;
  description: string;
} {
  const fromTier = ROLE_ACCESS_LEVEL[ctx.fromRoleSlug];
  const toTier = ROLE_ACCESS_LEVEL[ctx.toRoleSlug];
  const deptChanged = ctx.fromDeptName !== ctx.toDeptName;

  // Promote to Admin / Supervisor.
  if ((toTier === 'ADMIN' || toTier === 'SUPERVISOR') && fromTier !== toTier) {
    // Quote the role label (proper-cased from `ROLE_NAMES`) instead of
    // lower-casing it. `toLowerCase()` mangled acronym labels — e.g.
    // «Помощник ГИПа» rendered «назначить помощник гипа?» (un-declined,
    // de-capitalised ГИП). Quoting keeps the canonical label intact and
    // reads correctly for every ADMIN-tier role.
    return {
      title: `Назначить роль «${ctx.toRoleName}»?`,
      description: `Это создаст ещё одного администратора с правами «${ctx.toRoleName}». Продолжить?`,
    };
  }

  // Promote to MANAGER (РО). Занятый отдел сюда НЕ доходит — его отбивает
  // предпроверка `hasActiveManager` в родителе (фикс-пак Р-114), поэтому
  // копия говорит об одном руководителе, а не о «дополнительном».
  if (toTier === 'MANAGER' && fromTier !== 'MANAGER') {
    const where = ctx.toDeptName ? ` отдела «${ctx.toDeptName}»` : ' отдела';
    return {
      title: `Назначить руководителем отдела?`,
      description: `Сотрудник «${ctx.targetFullName}» станет руководителем${where} и получит права руководителя.`,
    };
  }

  // Demote РО → Сотрудник / EMPLOYEE-tier.
  //
  // ⚠️ Фикс-пак Р-114: прежний совет «Назначьте нового РО ЗАРАНЕЕ, иначе
  // изменение будет отклонено» НЕИСПОЛНИМ — назначить второго РО в тот же
  // отдел нельзя (триггер `user_one_active_ro_per_dept`, миграция 002),
  // так что порядок ровно обратный: сперва снять, потом назначить. И
  // отклоняет снятие не отсутствие преемника, а строки `ProjectManager`:
  // сервер отвечает 422 `last_manager_in_department` («руководит отделом
  // хотя бы в одном проекте»).
  if (fromTier === 'MANAGER' && toTier === 'EMPLOYEE') {
    return {
      title: `Снять с должности «${ctx.fromRoleName}»?`,
      // Три предложения: что потеряет · когда назначать нового · когда сервер
      // откажет (шлифовка 1 — прежняя редакция была одним предложением с
      // оборотом «Отклонено снятие будет по другой причине»; смысл не менялся).
      description:
        `Сотрудник «${ctx.targetFullName}» потеряет права руководителя отдела, и отдел останется без руководителя. ` +
        'Нового руководителя назначайте после этого изменения: двух действующих руководителей у отдела быть не может. ' +
        'Изменение не сохранится, если сотрудник руководит отделом хотя бы в одном проекте, — сначала назначьте туда другого руководителя.',
    };
  }

  // Department transfer (role tier unchanged).
  if (deptChanged && fromTier === toTier) {
    return {
      title: `Перевести в другой отдел?`,
      description: `Сотрудник «${ctx.targetFullName}» будет переведён${
        ctx.toDeptName ? ` в отдел «${ctx.toDeptName}»` : ' без отдела'
      }${ctx.fromDeptName ? ` (текущий: «${ctx.fromDeptName}»)` : ''}.`,
    };
  }

  // Generic fallback — role label changed but tier the same.
  return {
    title: `Изменить роль на «${ctx.toRoleName}»?`,
    description: `Сотрудник «${ctx.targetFullName}» — текущая роль: «${ctx.fromRoleName}». Подтвердите изменение.`,
  };
}

export function RoleTransitionAlertDialog(
  props: RoleTransitionAlertDialogProps,
): React.ReactElement | null {
  const { open, context, onConfirm, onCancel } = props;
  // Track which path closed the dialog so the auto-close `onOpenChange`
  // does not double-fire onCancel after a confirm (which would revert
  // the dirty form state we are about to PATCH).
  const closingViaRef = React.useRef<'confirm' | 'cancel' | null>(null);
  if (!context) return null;
  const { title, description } = describeTransition(context);

  const handleConfirm = (): void => {
    closingViaRef.current = 'confirm';
    onConfirm();
  };
  const handleCancel = (): void => {
    closingViaRef.current = 'cancel';
    onCancel();
  };
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        // If the close was driven by an explicit click we already routed
        // it; only the ESC / outside-click path needs to call onCancel.
        const via = closingViaRef.current;
        closingViaRef.current = null;
        if (via === null) onCancel();
      }}
    >
      <AlertDialogContent data-testid="role-transition-alert">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription data-testid="role-transition-description">
            {description}
          </AlertDialogDescription>
          {/*
            Блок 2 · Ф8 · В-Э, фикс-пак 1. Смена должности сбрасывает всё, что
            супервайзер добавил человеку (Р-198; отнять с 11.10.2026 нельзя; Р-207 —
            и когда должность меняет администратор). Сказано всем, кто видит
            диалог, а не только супервайзеру: сбрасывает действие, а не тот,
            кто выдавал. Перевод между отделами без смены должности прав не
            трогает — там строки нет.
          */}
          {context.fromRoleSlug !== context.toRoleSlug ? (
            <p className="text-sm text-muted" data-testid="role-transition-rights-reset">
              {rightsRu.resetWarning.roleChange}
            </p>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="role-transition-cancel" onClick={handleCancel}>
            Отмена
          </AlertDialogCancel>
          <AlertDialogAction data-testid="role-transition-confirm" onClick={handleConfirm}>
            Подтвердить
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
